import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useConfig } from "@/lib/useConfig";
import { useCategories } from "@/lib/categories";
import { useDesktop } from "@/lib/useDesktop";
import { loginWithTelegram, logoutWeb, type TelegramWidgetUser } from "@/lib/api";
import { clearWebSession, getWebProfile, getWebSession, setWebSession, type WebProfile } from "@/lib/webSession";
import type { Locale } from "@/types";
import { useDesktopNav } from "./desktopUi";
import { useDesktopMotion } from "@/lib/desktopMotion";
import { useNight } from "@/lib/night";
import { BaristaChat } from "./BaristaChat";
import { DesktopFlavors } from "@/pages/desktop/DesktopFlavors";
import { SearchPalette } from "./SearchPalette";
import { CartDrawer, openCartDrawer } from "./CartDrawer";
import { QuickView } from "./QuickView";
import { MegaMenu } from "./MegaMenu";
import { DesktopHome } from "@/pages/desktop/DesktopHome";
import { DesktopCatalog } from "@/pages/desktop/DesktopCatalog";
import { DesktopProduct } from "@/pages/desktop/DesktopProduct";
import { DesktopLead } from "@/pages/desktop/DesktopLead";
import { DesktopCart, DesktopCheckoutFrame } from "@/pages/desktop/DesktopCart";
import { DesktopAbout } from "@/pages/desktop/DesktopAbout";
import { DesktopPost, DesktopPosts } from "@/pages/desktop/DesktopBlog";
import { DesktopAddresses, DesktopFavorites, DesktopOrderDetail, DesktopOrderDone, DesktopOrders, DesktopPassport, DesktopProfile, DesktopTaste } from "@/pages/desktop/DesktopAccount";
import { DesktopCompare, DesktopEvents, DesktopFaq, DesktopGift, DesktopRecipe, DesktopRecipes, DesktopReviews, DesktopSubscription, DesktopTicket, DesktopWishlist } from "@/pages/desktop/DesktopExtras";

const svg = (d: string, size = 22, sw = 1.5) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />
);
const I = {
  search: svg('<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>', 17, 1.8),
  user: svg('<circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/>', 26, 1.4),
  cart: svg('<path d="M3 4h2l2.5 12h11L21 7H6.2"/><circle cx="9.5" cy="19.5" r="1.3"/><circle cx="17.5" cy="19.5" r="1.3"/>', 26, 1.4),
  moon: svg('<path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/>', 18, 1.7),
  sun: svg('<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>', 18, 1.7),
  tg: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.5 4.2 2.9 11.4c-1 .4-1 1.6.1 1.9l4.6 1.5 1.8 5.6c.2.8 1.2 1 1.8.4l2.6-2.4 4.8 3.5c.7.5 1.7.1 1.9-.8l3.2-15.2c.2-1-.8-1.8-1.7-1.5z" /></svg>,
};
const LOCALES: Locale[] = ["ru", "uz", "en"];

/** Loads the display font only in desktop mode, so the Mini App keeps loading exactly as before. */
function useDesktopFont(on: boolean) {
  useEffect(() => {
    if (!on || document.getElementById("kms-desktop-font")) return;
    const link = document.createElement("link");
    link.id = "kms-desktop-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Prata&display=swap";
    document.head.appendChild(link);
  }, [on]);
}

/** Telegram Login Widget in a modal. Same customer as in the Mini App. */
function LoginModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { botUsername } = useConfig();
  const box = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!botUsername || !box.current) return;
    (window as unknown as { kmsTelegramAuth: (u: TelegramWidgetUser) => void }).kmsTelegramAuth = async (user) => {
      try {
        const res = await loginWithTelegram(user);
        setWebSession(res.token, { firstName: res.customer.firstName, username: res.customer.username, photoUrl: res.customer.photoUrl });
        window.location.reload(); // contexts reload with the signed-in customer
      } catch {
        setError(t("desktop.loginFailed"));
      }
    };
    const s = document.createElement("script");
    s.src = "https://telegram.org/js/telegram-widget.js?22";
    s.async = true;
    s.setAttribute("data-telegram-login", botUsername);
    s.setAttribute("data-size", "large");
    s.setAttribute("data-radius", "12");
    s.setAttribute("data-request-access", "write");
    s.setAttribute("data-onauth", "kmsTelegramAuth(user)");
    box.current.innerHTML = "";
    box.current.appendChild(s);
  }, [botUsername]);
  return (
    <div className="dk-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="dk-modal__card" onClick={(e) => e.stopPropagation()}>
        <button className="dk-modal__x" onClick={onClose} aria-label={t("common.close")}>×</button>
        <img src="/brand/kms-logo.png" alt="KMS ROASTING" className="dk-modal__logo" />
        <h2 className="dk-serif">{t("desktop.loginTitle")}</h2>
        <p>{t("desktop.loginText")}</p>
        <div ref={box} className="dk-modal__widget" />
        {error && <div className="dk-modal__error">{error}</div>}
        {botUsername && (
          <a className="dk-modal__alt" href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">
            {t("desktop.openInTelegram")}
          </a>
        )}
      </div>
    </div>
  );
}

function Header({ onLogin, onSearch }: { onLogin: () => void; onSearch: () => void }) {
  const { t, locale, setLocale } = useI18n();
  const { route, navigate } = useRouter();
  const { totalItems } = useCart();
  const categories = useCategories();
  const nav = useDesktopNav(categories);
  const [night, toggleNight] = useNight();
  const [profile, setProfile] = useState<WebProfile | null>(getWebSession() ? getWebProfile() : null);
  useEffect(() => {
    const off = () => setProfile(null);
    window.addEventListener("kms:signed-out", off);
    return () => window.removeEventListener("kms:signed-out", off);
  }, []);
  const activeCat = route.name === "catalog" ? route.category : undefined;
  // Big menu: opens on hover over a catalogue item (short delay so passing the mouse over doesn't flash it).
  const [mega, setMega] = useState<string | null>(null);
  const megaTimer = useRef<number | undefined>(undefined);
  const hoverMega = (key: string | null, delay = key ? 140 : 220) => {
    window.clearTimeout(megaTimer.current);
    megaTimer.current = window.setTimeout(() => setMega(key), delay);
  };
  const keepMega = () => window.clearTimeout(megaTimer.current);
  useEffect(() => setMega(null), [route]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMega(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const megaGroup = mega && (mega === nav.equipment?.slug || mega === nav.accessories?.slug) ? mega : null;
  const megaSections = megaGroup ? (megaGroup === nav.equipment?.slug ? nav.equipment!.categories : nav.accessories!.categories) : nav.coffee;
  const signOut = async () => {
    await logoutWeb().catch(() => {});
    clearWebSession();
    window.location.reload();
  };
  return (
    <>
    <header className="dk-header">
      <div className="dk-header__in">
        <button className="dk-logo" onClick={() => navigate({ name: "home" })} aria-label="KMS ROASTING">
          <img src="/brand/kms-logo.png" alt="KMS ROASTING" />
        </button>
        <nav className="dk-nav">
          {nav.coffee.map((c) => (
            <button key={c.id} data-active={activeCat === c.slug} data-open={mega === c.slug} onMouseEnter={() => hoverMega(c.slug)} onMouseLeave={() => hoverMega(null)} onClick={() => navigate({ name: "catalog", category: c.slug })}>
              {c.name}
            </button>
          ))}
          {nav.equipment && <button data-open={mega === nav.equipment.slug} onMouseEnter={() => hoverMega(nav.equipment!.slug)} onMouseLeave={() => hoverMega(null)} data-active={activeCat === nav.equipment.slug || nav.equipment.categories.some((c) => c.slug === activeCat)} onClick={() => navigate({ name: "catalog", category: nav.equipment!.slug })}>{t("desktop.equipment")}</button>}
          {nav.accessories && <button data-open={mega === nav.accessories.slug} onMouseEnter={() => hoverMega(nav.accessories!.slug)} onMouseLeave={() => hoverMega(null)} data-active={activeCat === nav.accessories.slug || nav.accessories.categories.some((c) => c.slug === activeCat)} onClick={() => navigate({ name: "catalog", category: nav.accessories!.slug })}>{t("desktop.accessories")}</button>}
          <button data-active={route.name === "posts" || (route.name === "post" && route.slug !== "about")} onClick={() => navigate({ name: "posts" })}>{t("desktop.blog")}</button>
          <button data-active={route.name === "post" && route.slug === "about"} onClick={() => navigate({ name: "post", slug: "about" })}>{t("desktop.about")}</button>
          <button data-active={route.name === "lead" || route.name === "b2b"} onClick={() => navigate({ name: "lead", source: "site_header" })}>{t("profile.b2b")}</button>
        </nav>
        <div className="dk-header__right">
          <button className="dk-search" onClick={onSearch} aria-label={t("desktop.search")}>
            <span className="dk-search__ph">{t("desktop.searchPlaceholder")}</span>
            <kbd>Ctrl K</kbd>
            <span className="dk-search__ic">{I.search}</span>
          </button>
          <div className="dk-langs">
            {LOCALES.map((l) => (
              <button key={l} data-active={l === locale} onClick={() => setLocale(l)}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button className="dk-night-btn" onClick={toggleNight} title={night ? t("desktop.dayMode") : t("desktop.nightMode")} aria-label={night ? t("desktop.dayMode") : t("desktop.nightMode")}>
            {night ? I.sun : I.moon}
          </button>
          {profile ? (
            <div className="dk-account">
              <button className="dk-icon" onClick={() => navigate({ name: "profile" })} title={t("profile.title")}>{I.user}<span>{profile.firstName}</span></button>
              <button className="dk-link" onClick={signOut}>{t("desktop.signOut")}</button>
            </div>
          ) : (
            <button className="dk-signin" onClick={onLogin}>{I.tg}{t("desktop.signIn")}</button>
          )}
          <button className="dk-icon dk-cart" onClick={() => (route.name === "cart" || route.name === "checkout" ? navigate({ name: "cart" }) : openCartDrawer())} aria-label={t("cart.title")}>
            {I.cart}
            {totalItems > 0 && <b>{totalItems}</b>}
          </button>
        </div>
      </div>
    </header>
    {mega && <MegaMenu open={mega} sections={megaSections} groupSlug={megaGroup} onEnter={keepMega} onClose={() => hoverMega(null)} />}
    {mega && <div className="mm-shade" onMouseEnter={() => hoverMega(null, 0)} aria-hidden="true" />}
    </>
  );
}

function Footer() {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const { botUsername, site } = useConfig();
  const categories = useCategories();
  return (
    <footer className="dk-footer">
      <div className="dk-footer__in">
        <div>
          <img src="/brand/kms-logo.png" alt="KMS ROASTING" className="dk-footer__logo" />
          <div className="dk-footer__motto">{t("desktop.motto")}</div>
        </div>
        <div>
          <h5>{t("desktop.catalog")}</h5>
          {(categories ?? []).slice(0, 5).map((c) => (
            <button key={c.id} onClick={() => navigate({ name: "catalog", category: c.slug })}>{c.name}</button>
          ))}
          <button onClick={() => navigate({ name: "flavors" })}>{t("desktop.fwTitle")}</button>
        </div>
        <div>
          <h5>{t("desktop.company")}</h5>
          <button onClick={() => navigate({ name: "posts" })}>{t("desktop.blog")}</button>
          <button onClick={() => navigate({ name: "lead", source: "site_footer" })}>{t("profile.b2b")}</button>
          <button onClick={() => navigate({ name: "faq" })}>{t("faq.title")}</button>
        </div>
        <div>
          <h5>{t("desktop.help")}</h5>
          <button onClick={() => navigate({ name: "faq", category: "delivery" })}>{t("desktop.delivery")}</button>
          <button onClick={() => navigate({ name: "faq", category: "order" })}>{t("desktop.payment")}</button>
          <button onClick={() => navigate({ name: "faq", category: "returns" })}>{t("desktop.returns")}</button>
        </div>
        <div className="dk-footer__sub">
          <h5>{t("desktop.stayTuned")}</h5>
          <p>{t("desktop.stayTunedText")}</p>
          <div className="dk-footer__row">
            {(site?.telegramChannel || botUsername) && (
              <a className="dk-footer__tg" href={site?.telegramChannel || `https://t.me/${botUsername}`} target="_blank" rel="noreferrer">{I.tg}{t("desktop.subscribe")}</a>
            )}
            <div className="dk-footer__soc">
              {site?.instagram && <a href={site.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" /></svg></a>}
              {(site?.telegramChannel || botUsername) && <a href={site?.telegramChannel || `https://t.me/${botUsername}`} target="_blank" rel="noreferrer" aria-label="Telegram">{I.tg}</a>}
              {site?.youtube && <a href={site.youtube} target="_blank" rel="noreferrer" aria-label="YouTube"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M22 8.2a3 3 0 0 0-2.1-2.1C18 5.6 12 5.6 12 5.6s-6 0-7.9.5A3 3 0 0 0 2 8.2 31 31 0 0 0 1.6 12 31 31 0 0 0 2 15.8a3 3 0 0 0 2.1 2.1c1.9.5 7.9.5 7.9.5s6 0 7.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .4-3.8 31 31 0 0 0-.4-3.8zM10 15V9l5.2 3z" /></svg></a>}
            </div>
          </div>
          <div className="dk-footer__legal">
            <span>© {new Date().getFullYear()} KMS Roasting</span>
            <button onClick={() => navigate({ name: "post", slug: "privacy" })}>{t("profile.privacy")}</button>
            <button onClick={() => navigate({ name: "post", slug: "offer" })}>{t("profile.offer")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Desktop site frame: header + footer around the app's pages. Only in a regular
 * browser on a wide screen; inside Telegram it renders its children unchanged.
 */
export function DesktopFrame({ children }: { children: React.ReactNode }) {
  const desktop = useDesktop();
  const { t } = useI18n();
  const { route } = useRouter();
  const [login, setLogin] = useState(false);
  const [search, setSearch] = useState(false);
  const page = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!desktop) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      if ((e.key === "k" || e.key === "K" || e.key === "л" || e.key === "Л") && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setSearch(true);
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        setSearch(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [desktop]);
  useDesktopFont(desktop);
  useDesktopMotion(desktop, page, JSON.stringify(route));
  useEffect(() => {
    const open = () => setLogin(true);
    window.addEventListener("kms:login", open);
    return () => window.removeEventListener("kms:login", open);
  }, []);
  const [night] = useNight();
  useEffect(() => {
    document.documentElement.classList.toggle("dk-mode", desktop);
  }, [desktop]);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dk-night", desktop && night);
    // Mini App components inside desktop pages follow the site's theme.
    if (desktop) root.setAttribute("data-theme", night ? "dark" : "light");
  }, [desktop, night]);
  useEffect(() => {
    if (desktop) window.scrollTo(0, 0);
  }, [route, desktop]);
  if (!desktop) return <>{children}</>;
  return (
    <div className="dk-root">
      <Header onLogin={() => setLogin(true)} onSearch={() => setSearch(true)} />
      <div ref={page} className="dk-page">
        {route.name === "home" || route.name === "catalog" || route.name === "product" || route.name === "cart" ? (
          <main className="dk-main dk-main--full">
            {route.name === "home" ? <DesktopHome /> : route.name === "catalog" ? <DesktopCatalog /> : route.name === "product" ? <DesktopProduct /> : <DesktopCart />}
          </main>
        ) : route.name === "posts" || route.name === "post" ? (
          <main className="dk-main dk-main--full">
            {route.name === "posts" ? <DesktopPosts /> : route.slug === "about" ? <DesktopAbout /> : <DesktopPost slug={route.slug} />}
          </main>
        ) : route.name === "lead" || route.name === "b2b" ? (
          <main className="dk-main dk-main--full">
            <DesktopLead />
          </main>
        ) : route.name === "checkout" ? (
          <main className="dk-main dk-main--full">
            <DesktopCheckoutFrame>{children}</DesktopCheckoutFrame>
          </main>
        ) : route.name === "flavors" ? (
          <main className="dk-main dk-main--full">
            <DesktopFlavors />
          </main>
        ) : route.name === "orderConfirmation" ? (
          <main className="dk-main dk-main--full">
            <DesktopOrderDone orderId={route.orderId} />
          </main>
        ) : route.name === "profile" || route.name === "orders" || route.name === "orderDetail" || route.name === "favorites" ? (
          <main className="dk-main dk-main--full">
            {route.name === "profile" ? <DesktopProfile /> : route.name === "orders" ? <DesktopOrders /> : route.name === "orderDetail" ? <DesktopOrderDetail orderId={route.orderId} /> : <DesktopFavorites />}
          </main>
        ) : route.name === "addresses" || route.name === "taste" || route.name === "passport" ? (
          <main className="dk-main dk-main--full">
            {route.name === "addresses" ? <DesktopAddresses /> : route.name === "taste" ? <DesktopTaste /> : <DesktopPassport />}
          </main>
        ) : route.name === "faq" || route.name === "recipes" || route.name === "recipe" || route.name === "events" || route.name === "ticket" || route.name === "reviews" || route.name === "compare" || route.name === "wishlist" || route.name === "gift" || route.name === "subscription" ? (
          <main className="dk-main dk-main--full">
            {route.name === "faq" ? <DesktopFaq category={route.category} />
              : route.name === "recipes" ? <DesktopRecipes />
              : route.name === "recipe" ? <DesktopRecipe slug={route.slug} />
              : route.name === "events" ? <DesktopEvents />
              : route.name === "ticket" ? <DesktopTicket id={route.id} />
              : route.name === "reviews" ? <DesktopReviews slug={route.slug} />
              : route.name === "compare" ? <DesktopCompare />
              : route.name === "wishlist" ? <DesktopWishlist code={route.code} />
              : route.name === "gift" ? <DesktopGift code={route.code} />
              : <DesktopSubscription id={route.id} />}
          </main>
        ) : (
          <main className="dk-main">
            {/* Pages without their own desktop design are shown as a neat centred column. */}
            <div className="dk-column">{children}</div>
          </main>
        )}
      </div>
      <Footer />
      <BaristaChat />
      <SearchPalette open={search} onClose={() => setSearch(false)} />
      <CartDrawer />
      <QuickView />
      {login && <LoginModal onClose={() => setLogin(false)} />}
    </div>
  );
}
